import React from "react";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { PRODUCTS, Product, productHref } from "@/data/products";
import { useContact } from "@/context/ContactContext";
import { trackEvent } from "@/utils/analytics";
import "./ProductsSection.scss";

interface ProductContent {
  name: string;
  tagline: string;
  description: string;
  highlights: string[];
  priceLabel: string;
  priceNote: string;
  timeline: string;
  ctaLabel: string;
  contactLabel: string;
  siteNote: string;
  imageAlt: string;
}

interface ProductsSectionProps {
  /** Defaults to every product in src/data/products.ts. */
  products?: readonly Product[];
}

const ExternalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M7 3H3v10h10V9M10 2h4v4M14 2L8 8" />
  </svg>
);

/** Screenshot of the live product inside a minimal browser frame. */
const ProductVisual: React.FC<{ product: Product; alt: string; sizes: string }> = ({
  product,
  alt,
  sizes,
}) => (
  <div className="prod-browser">
    <div className="prod-browser-bar" aria-hidden="true">
      <span className="prod-browser-dots">
        <span />
        <span />
        <span />
      </span>
      <span className="prod-browser-url">{new URL(product.url).host}</span>
    </div>
    <picture className="prod-shot">
      <source
        type="image/webp"
        srcSet={`${product.img.webp} 640w, ${product.img.webp2x} 1280w`}
        sizes={sizes}
      />
      <img
        src={product.img.fallback}
        srcSet={`${product.img.fallback} 1280w`}
        sizes={sizes}
        alt={alt}
        width={product.img.width}
        height={product.img.height}
        loading="lazy"
        decoding="async"
      />
    </picture>
  </div>
);

const ProductBody: React.FC<{ product: Product; content: ProductContent }> = ({
  product,
  content,
}) => {
  const { t } = useTranslation();
  const { requestContact } = useContact();

  return (
    <div className="prod-body">
      {product.status === "new" && (
        <span className="prod-badge">{t("products.newBadge")}</span>
      )}
      <h3 className="prod-name">{content.name}</h3>
      <p className="prod-tagline">{content.tagline}</p>
      <p className="prod-desc">{content.description}</p>

      <ul className="prod-highlights">
        {content.highlights.slice(0, 4).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>

      <div className="prod-meta">
        <div className="prod-price-wrap">
          <span className="prod-price">{content.priceLabel}</span>
          <span className="prod-price-note">{content.priceNote}</span>
        </div>
        <span className="prod-timeline">{content.timeline}</span>
      </div>

      <ul className="prod-tags">
        {product.tags.map((tag) => (
          <li key={tag}>{tag}</li>
        ))}
      </ul>

      <div className="prod-actions">
        <a
          className="prod-btn prod-btn--primary"
          href={productHref(product)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent("product_cta", { product: product.slug })}
        >
          {content.ctaLabel} <ExternalIcon />
          <span className="prod-sr"> ({t("products.newTabNote")})</span>
        </a>
        <button
          type="button"
          className="prod-btn prod-btn--secondary"
          onClick={() => {
            trackEvent("product_contact", { product: product.slug });
            requestContact(content.contactLabel);
          }}
        >
          {t("products.talkCta")}
        </button>
        {content.siteNote && <span className="prod-site-note">{content.siteNote}</span>}
      </div>
    </div>
  );
};

const ProductsSection: React.FC<ProductsSectionProps> = ({ products = PRODUCTS }) => {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  if (products.length === 0) return null;

  const items = products.map((product) => ({
    product,
    content: t(`products.items.${product.key}`, { returnObjects: true }) as ProductContent,
  }));

  const title = t("products.title");
  const hl = t("products.titleHighlight");
  const [before, after = ""] = title.split(hl);

  return (
    <section
      className="products-section"
      id="products-section"
      ref={ref}
      aria-labelledby="products-section-title"
    >
      <div className="container">
        <div className={`prod-head reveal${isVisible ? " in" : ""}`}>
          <span className="sec-label">{t("products.label")}</span>
          <h2 className="sec-title" id="products-section-title">
            {before}
            <em>{hl}</em>
            {after}
          </h2>
          <p className="prod-subtitle">{t("products.subtitle")}</p>
        </div>

        {items.length === 1 ? (
          <article
            className={`prod-card prod-card--showcase reveal${isVisible ? " in" : ""}`}
            style={{ transitionDelay: "0.1s" }}
            data-product={items[0].product.slug}
          >
            <div className="prod-visual">
              <ProductVisual
                product={items[0].product}
                alt={items[0].content.imageAlt}
                sizes="(max-width: 960px) 100vw, 720px"
              />
            </div>
            <ProductBody product={items[0].product} content={items[0].content} />
          </article>
        ) : (
          <ul className="prod-grid" aria-label={t("products.label")}>
            {items.map(({ product, content }, i) => (
              <li
                key={product.slug}
                className={`prod-card prod-card--grid reveal${isVisible ? " in" : ""}`}
                style={{ transitionDelay: `${0.1 + i * 0.08}s` }}
                data-product={product.slug}
              >
                <div className="prod-visual">
                  <ProductVisual
                    product={product}
                    alt={content.imageAlt}
                    sizes="(max-width: 640px) 100vw, 420px"
                  />
                </div>
                <ProductBody product={product} content={content} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
